<template>
  <DsButton
    class="toggle-panel-btn"
    variant="primary"
    :icon="isSidePanelOpen ? 'chevron-left' : 'chevron-right'"
    icon-fill-color="white"
    size="small"
    @click="toggleSidePanel"
  />
  <div class="conversational-body__conversations-list-section">
    <div class="conversational-body__conversations-list-section-header">
      <p style="margin-left: 1rem;"><strong>This isn't just a search. It's a conversation with your ecosystem.</strong></p>
      <div style="padding-right: 2rem">
        <DsButton
          @click="$emit('create-conversation')"
          class="new-conversation-button"
          icon="plus"
          label="Ask Foodleap"
          variant="rounded"
          style="text-align: center; justify-content: center;"
        />
      </div>
      <!--                <p style="margin-left: 1rem;"><strong>This isn't just a search. It's a conversation with your ecosystem.</strong></p>-->
      <div class="divider" style="margin-bottom: 1rem; width: 100%"></div>
      <div class="search-container">
        <DsInput
          icon="search"
          placeholder="Search conversations..."
          v-model="searchQuery"
          class="search-input"
          is-simplified
        />
      </div>
    </div>
    <div class="conversational-body__conversations-list-items">
      <div
        v-for="conversation in filteredConversations"
        :key="conversation.id"
        @click="$emit('select-conversation', String(conversation.session_id ?? conversation.id))"
        :class="['conversation-item', { 'active': conversation.id === activeConversationId }]"
      >
        <div class="conversation-item__content">
          <div class="conversation-texts">
            <div class="conversation-title-actions" v-if="showTitleEdit !== conversation.id">
              <h3 class="title">{{ conversation.title ?? 'New Exploration' }}</h3>
              <DsButton
                v-if="conversation.session_id"
                icon="edit"
                variant="rounded"
                size="extra-small"
                class="edit-button"
                @click.stop="startTitleEdit(conversation.id)"
              />
            </div>
            <DsInput
              v-else
              placeholder="Title of conversation..."
              is-simplified
              v-model="conversation.title"
              style="min-width: 200px;"
              @pressed-enter="updateConversationTitle(conversation.id, conversation.title)"
              :ref="el => { if (el) titleInputs[conversation.id] = el }"
              @click.stop
              @blur="updateConversationTitle(conversation.id, conversation.title); showTitleEdit = null"
            />
            <span class="timestamp">{{ formatTimestamp(conversation.timestamp) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
  import { defineComponent } from 'vue'
  import DsInput from '@/components/Form/DsInput.vue'
  import ConversationalAgentMixin from '@/Domain/ConversationalAgent/Mixins/ConversationalAgentMixin'
  import { getUserConversations, updateConversationTitle } from '@/Domain/ConversationalAgent/Api/conversationalAgent'
  import moment from 'moment'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import { Conversation } from '@/Domain/ConversationalAgent/Types/types'

  export default defineComponent({
    name: "ConversationalAgentSidePanel",
    components: { DsInput, DsButton },
    mixins: [ConversationalAgentMixin],
    emits: ['select-conversation', 'create-conversation', 'toggle-panel'],
    props: {
      activeConversationId: {
        type: String,
        default: ''
      }
    },
    data() {
      return {
        showTitleEdit: null as string | null,
        titleInputs: {} as Record<string, any>,
        searchQuery: '',
        conversations: [] as Conversation[],
        isSidePanelOpen: true,
      }
    },
    computed: {
      filteredConversations() {
        const query = this.searchQuery.toLowerCase()

        if (!query) {
          return this.conversations
        }

        return this.conversations.map(conv => ({
          ...conv,
          isActive: String(conv.session_id ?? conv.id) === this.activeConversationId
        })).filter(conv => {
          let title = conv.title ?? 'New Exploration'
          return title.toLowerCase().includes(query)
        })
      }
    },
    methods: {
      toggleSidePanel() {
        this.isSidePanelOpen = !this.isSidePanelOpen;
        this.$emit('toggle-panel');
      },
      updateConversationTitle(id, title) {
        updateConversationTitle(id, title)
          .then(() => {
            // Title updated successfully
          })
          .finally(() => {
            this.showTitleEdit = null
          })
      },
      startTitleEdit(id: string) {
        this.showTitleEdit = id;
        this.$nextTick(() => {
          const input = this.titleInputs[id];
          if (input && input.$el) {
            const inputElement = input.$el.querySelector('input');
            if (inputElement) {
              inputElement.focus();
            }
          }
        });
      },
      formatTimestamp(timestamp: string | undefined) {
        const momentDate = moment(timestamp);
        return momentDate.isValid() ? momentDate.fromNow() : 'Just now';
      },
      fetchConversations() {
        return getUserConversations()
          .then((response) => {
            this.conversations = response.map(conversation => {
              return {
                id: conversation.id,
                title: conversation.title,
                lastMessage: '',
                timestamp: conversation.create_time,
                session_id: conversation.id,
                messages: [],
                isActive: false
              }
            })
          })
      }
    },
    watch: {
      showTitleEdit(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            const input = this.$el.querySelector('.conversation-title-actions + .ds-input input');
            if (input) {
              (input as HTMLInputElement).focus();
            }
          });
        }
      }
    },
    mounted() {
      this.fetchConversations();
    }
  })
</script>


<style scoped lang="scss">
  .conversational-body__conversations-list-section {
    display: flex;
    flex-direction: column;
    max-width: 100%;
    height: 75vh;
    margin-top: 2rem;

    h4 {
      margin-left: 1rem;
    }
  }

  .conversational-body__conversations-list-section-header {
    .new-conversation-button {
      display: flex;
      width: 100%;
      align-items: center;
      gap: 0.5rem;
      background-color: #f3f1ff;
      color: #843dff;
      margin: 1rem;

      &:hover {
        background-color: #843DFF;

        :deep(.svg-icon svg path) {
          fill: white;
        }
      }

      :deep(.svg-icon svg path) {
        fill: #843dff;
      }
    }

    .search-container {
      position: relative;
      margin: 1rem;

      :deep(.ds-input) {
        input:focus {
          outline: none;
          border-color: #BEA6FF;
        }
      }
    }
  }

  .conversational-body__conversations-list-items {
    overflow-y: auto;

    .conversation-item {
      padding: 1rem;
      cursor: pointer;
      border-bottom: 1px solid #f0f0f0;
      transition: background-color 0.2s, border-left 0.2s;

      &.active {
        background-color: #F3F1FF;
        border-left: 4px solid #BEA6FF;
      }

      &:hover {
        background-color: #F3F1FF;
      }

      .conversation-item__content {
        display: flex;
        gap: 0.75rem;
        width: 100%;
      }

      .conversation-texts {
        min-width: 0;
        display: flex;
        flex-direction: column;

        .conversation-title-actions {
          display: flex;
          flex-direction: row;

          .title {
            font-size: 14px;
          }
        }

        .edit-button {
          align-self: flex-end;
          margin-left: 0.5rem;
          color: #BEA6FF;

          &:hover {
            color: #843DFF;
          }
        }

        .title {
          font-weight: 600;
          color: #1f2937;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .timestamp {
          font-size: 0.75rem;
          color: #9ca3af;
          margin-top: 0.25rem;
        }

        :deep(.ds-input) {

        }
      }
    }
  }

  .toggle-panel-btn {
    position: absolute;
    right: -38px;
    top: 0.5rem;
    z-index: 3;
    border-radius: 0px 4px 4px 0px;
    box-shadow: 3px 2px 6px rgba(0, 0, 0, 0.05);
    padding: 0.25rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    background-color: #843DFF;
    color: white;

    &:hover {
      background-color: #843DFF;
    }

    :deep(.icon) {
      color: white;
    }
  }
</style>
